import React from "react";
import styled from "styled-components";
import { Box } from "../Box";
import { Icon } from "../Icon";
import { style } from "../style";

export const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${style.vars.space[1]};
`;

export const IconWrapper = styled.div`
  min-width: 24px;
`;

export const Text = styled.div`
  font-weight: ${style.vars.fontWeight.bold};
  font-size: ${style.vars.fontSize.small};
  color: ${style.colors.red.dark};
`;

type Props = {
  message: React.ReactNode;
};

const InputErrorMessage: React.FC<Props> = (props) => {
  return (
    <Container>
      <IconWrapper>
        <Icon large icon="error" />
      </IconWrapper>
      <Text data-testid="error-message" aria-label={`Error: ${props.message}`}>
        {props.message}
      </Text>
    </Container>
  );
};

export type ErrorLike = {
  message: string;
  validationMessages?: never;
};

export type GroupedErrors = {
  some: boolean;
  key: string;
  input: React.ReactElement | null;
  other: React.ReactElement[];
  validationMessages: ValidationMessages;
};

type InputElement = HTMLInputElement | HTMLTextAreaElement;

type ValidationMessages = {
  [K in keyof ValidityState]?: K extends "customError" | "invalid" ? undefined : string | ((input: InputElement) => string);
};

export type ObjectWithValidationMessages = {
  validationMessages: ValidationMessages;
};

type ErrorLikeWithValidationMessages = {
  message?: string | null;
  validationMessages: ValidationMessages;
};

export type PropError = true | null | undefined | string | ErrorLike | ErrorLikeWithValidationMessages | React.ReactElement;

function isReactElement(error: PropError): error is React.ReactElement {
  return React.isValidElement(error);
}

function getErrorElement(error?: PropError): React.ReactElement | undefined {
  if (!error || error === true) {
    return undefined;
  }

  if (typeof error === "string") {
    return <span>{error}</span>;
  }

  if (isReactElement(error)) {
    return error;
  }

  const message = error.message;

  if (!message) {
    return undefined;
  }

  return <span>{message}</span>;
}

function getValidationMessages(error?: PropError): ValidationMessages | undefined {
  if (!error || error === true || typeof error === "string" || isReactElement(error)) {
    return undefined;
  }

  return error.validationMessages;
}

export const PropError = {
  groupAll(other0: PropError | PropError[], inputErrorMessage: string | null, disabled: boolean): GroupedErrors {
    const other1 = PropError.asArray(other0);

    if (disabled) {
      return {
        some: false,
        key: "",
        input: null,
        other: [],
        validationMessages: {},
      };
    }

    let key = `${inputErrorMessage}/${other1.length}`;
    let some = false;
    const validationMessages: ValidationMessages = {};
    const other: React.ReactElement[] = [];
    let input: React.ReactElement | null = null;

    if (inputErrorMessage) {
      key = inputErrorMessage;
      input = <span>{inputErrorMessage}</span>;
      some = true;
    }

    for (const propError of other1) {
      Object.assign(validationMessages, getValidationMessages(propError));

      if (propError === true) {
        some = true;
      }

      const error = getErrorElement(propError);

      if (!error) {
        continue;
      }

      some = true;
      other.push(error);
    }

    return {
      some,
      key,
      input,
      other,
      validationMessages,
    };
  },

  asArray(error: PropError | PropError[]): PropError[] {
    if (Array.isArray(error)) {
      return error;
    }

    if (!error) {
      return [];
    }

    return [error];
  },

  getValidationMessageForInput(input: InputElement, params: ObjectWithValidationMessages): string {
    /**
     * The custom error should always take precedence over other error messages,
     * so if the input has a custom error, then we'll just use that.
     */
    if (input.validity.customError) {
      return input.validationMessage;
    }

    /**
     * When the input is invalid, check that we have a validation message for the specific
     * error that occurred. If we don't, then we'll just use the default validation message.
     */
    for (const key0 in params.validationMessages) {
      const key = key0 as keyof ValidityState;
      let message = params.validationMessages[key];

      if (key === "customError" || key === "valid") continue;
      if (message === undefined) continue;
      if (input.validity[key] === false) continue;

      if (typeof message === "function") {
        message = message(input);
      }

      return message;
    }

    return input.validationMessage;
  },

  renderForAfterInput(error?: PropError) {
    if (!error) {
      return null;
    }

    const message = getErrorElement(error);

    return (
      <Box display="block" mt={1}>
        <InputErrorMessage message={message} />
      </Box>
    );
  },

  renderGroupAfterInput(errors: GroupedErrors) {
    return (
      <>
        {PropError.renderForAfterInput(errors.input)}

        {errors.other.map((error, index) => (
          <React.Fragment key={index} children={PropError.renderForAfterInput(error)} />
        ))}
      </>
    );
  },
};

export type PublicPropError = PropError;

export const PublicPropError: Pick<typeof PropError, "renderForAfterInput"> = {
  renderForAfterInput: PropError.renderForAfterInput,
};
